// src/pages/HomePage.jsx
import React, { useEffect, useState } from "react";
import Sidebar from "./components/Sidebar";
import { IoHomeOutline } from "react-icons/io5";
import { Link } from "react-router-dom";
import { isLightOrganizationView } from "./utils/adminOrganizationView";

const API = process.env.REACT_APP_API_URL;

function PatientCard({ patient }) {
  const fullName = `${patient.forename} ${patient.surname}`;
  const isOnline = !!patient.is_online;
  const isDeviceOnline = !!patient.is_device_online;
  // Light organization: no red offline cards and no "Zuletzt online".
  const light = isLightOrganizationView();
  const formatLastSeen = (value) => {
    if (!value) return "unbekannt";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "unbekannt";
    return date.toLocaleString("de-DE");
  };
  const cardClass = isDeviceOnline || light
    ?"flex items-start gap-4 rounded-xl border border-black/20 bg-white/80 p-4 shadow-sm"
    : "flex items-start gap-4 rounded-xl border border-red-300 bg-red-100/30 p-4 shadow-sm";
  const lastSeenClass = isDeviceOnline ? "text-gray-600 font-normal" : "text-red-700 font-normal";
  return (
    <Link
      to={`/patients/${patient.id}`}
    >
    <div className={cardClass}>
      {/* Avatar circle */}
      <div className="h-12 w-12 flex items-center justify-center rounded-full border-2 border-black/80">
        {/* simple user glyph */}
        <svg viewBox="0 0 24 24" className="h-7 w-7">
          <circle cx="12" cy="8" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M4 19c1.8-3 4.6-4.5 8-4.5S18.2 16 20 19" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>

      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{fullName}</h3>
          <button className="h-6 w-6 rounded-full hover:bg-black/5 flex items-center justify-center">
            <span className="sr-only">More</span>
            <svg viewBox="0 0 24 24" className="h-4 w-4">
              <circle cx="5" cy="12" r="1.7" />
              <circle cx="12" cy="12" r="1.7" />
              <circle cx="19" cy="12" r="1.7" />
            </svg>
          </button>
        </div>

        <div className="mt-1 text-xs">
          {isOnline ? (
            <div className="text-green-700 font-normal">Online</div>
          ) : !light && (
            <div className={lastSeenClass}>
              Zuletzt online: {formatLastSeen(patient.last_seen_at)}
            </div>
          )}
        </div>
      </div>
    </div>
</Link>
  );
}

export default function HomePage() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let ignore = false;

    async function fetchPatients() {
      if (!ignore && patients.length === 0) {
        setLoading(true);
      }
      setErr("");
      try {
        const res = await fetch(`${API}/patients`, { credentials: "include" });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || "Failed to load patients");
        }
        const data = await res.json(); // expecting [{id, forename, surname, ...}, ...]
        if (!ignore) setPatients(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!ignore) setErr(e.message || "Error loading patients");
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    fetchPatients();
    const interval = window.setInterval(fetchPatients, 15000);

    return () => {
      ignore = true;
      window.clearInterval(interval);
    };
  }, [patients.length]);

  return (
    <div className="flex min-h-screen bg-[#cdeaff]">
      <Sidebar active="alle-bewohner" />
      <main className="flex-1 px-0 pt-6 pb-0">
        <div className="w-full h-full rounded-tl-xl border border-black/10 bg-white/60 p-6 backdrop-blur">
          <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold text-gray-900">Bewohner Übersicht</h1>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-800">
              <IoHomeOutline />
              <span className="text-lg">{patients.length}</span>
              <span className="text-lg">Nutzer</span>
            </div>

          </div>
        </div>

          {/* status messages */}
          {loading && (
            <div className="mt-6 text-sm text-gray-700">Lade Bewohner…</div>
          )}
          {err && !loading && (
            <div className="mt-6 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              {err}
            </div>
          )}
          {!loading && !err && patients.length === 0 && (
            <div className="mt-6 text-sm text-gray-700">
              Noch keine Bewohner vorhanden.
            </div>
          )}

          {/* cards */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {patients.map((p) => (
              <PatientCard key={p.id} patient={p} />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
